import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export interface Notification {
  id: string;
  recipient_user_id: string;
  actor_user_id: string;
  type: 'diary_like' | 'material_like' | 'diary_comment' | 'material_comment' | 'group_goal_achieved' | 'achievement_like' | 'achievement_posted' | 'chat_message';
  target_id: string;
  target_type: 'diary' | 'material' | 'group' | 'achievement';
  target_title: string | null;
  is_read: boolean;
  created_at: string;
  actor_username?: string;
  actor_avatar_url?: string;
}

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const notifs = data || [];
      const actorIds = [...new Set(notifs.map((n: Notification) => n.actor_user_id))];

      let profileMap: Record<string, { username: string; avatar_url?: string }> = {};
      if (actorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url')
          .in('user_id', actorIds);
        (profiles || []).forEach((p: { user_id: string; username: string; avatar_url?: string }) => {
          profileMap[p.user_id] = { username: p.username, avatar_url: p.avatar_url };
        });
      }

      const enriched: Notification[] = notifs.map((n: Notification) => ({
        ...n,
        actor_username: profileMap[n.actor_user_id]?.username || '不明なユーザー',
        actor_avatar_url: profileMap[n.actor_user_id]?.avatar_url,
      }));

      setNotifications(enriched);
      setUnreadCount(enriched.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('通知取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_user_id', user.id)
      .eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user]);

  const markAsRead = useCallback(async (notifId: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notifId);
    setNotifications(prev =>
      prev.map(n => n.id === notifId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [fetchNotifications, user]);

  // リアルタイム購読（アプリ全体で1つだけ）
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`notifications_global:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newNotif = payload.new as Notification;
          // 送信者のプロフィールを取得
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('user_id, username, avatar_url')
            .eq('user_id', newNotif.actor_user_id)
            .maybeSingle();

          const enriched: Notification = {
            ...newNotif,
            actor_username: profile?.username || '不明なユーザー',
            actor_avatar_url: profile?.avatar_url,
          };

          setNotifications(prev => {
            // 重複チェック
            if (prev.find(n => n.id === enriched.id)) return prev;
            return [enriched, ...prev];
          });
          setUnreadCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as Notification;
          setNotifications(prev =>
            prev.map(n => n.id === updated.id ? { ...n, is_read: updated.is_read } : n)
          );
          // 既読数を再計算
          setNotifications(prev => {
            const count = prev.filter(n => !n.is_read).length;
            setUnreadCount(count);
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount, loading, fetchNotifications, markAllAsRead, markAsRead }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used within NotificationsProvider');
  return ctx;
}
